// MaturaCircleCenterPointEquationClosedTask.js
// Zadanie maturalne zamknięte: równanie okręgu ze środka i punktu na okręgu.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const VECTOR_CASES = [
  { dx: 2, dy: 4 },
  { dx: -3, dy: 4 },
  { dx: 5, dy: -2 },
  { dx: -1, dy: 5 },
  { dx: 4, dy: -4 },
  { dx: -6, dy: 2 },
  { dx: 3, dy: 3 },
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[randInt(0, arr.length - 1)];

function randNonZero(min, max) {
  let value = 0;
  while (value === 0) value = randInt(min, max);
  return value;
}

function simplifyRoot(n) {
  let coeff = 1;
  let rest = n;
  for (let p = 2; p * p <= rest; p += 1) {
    while (rest % (p * p) === 0) {
      coeff *= p;
      rest /= p * p;
    }
  }

  if (rest === 1) return String(coeff);
  return coeff === 1 ? `\\sqrt{${rest}}` : `${coeff}\\sqrt{${rest}}`;
}

function shiftedSquare(variable, value) {
  if (value === 0) return `${variable}^2`;
  return value > 0
    ? `(${variable}-${value})^2`
    : `(${variable}+${Math.abs(value)})^2`;
}

function equationLatex(center, rightSideLatex) {
  return `${shiftedSquare("x", center.x)}+${shiftedSquare("y", center.y)}=${rightSideLatex}`;
}

function pointLatex(point) {
  return `(${point.x},${point.y})`;
}

function shuffle(items) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function makeOptions(center, radiusSquared, radiusLatex) {
  const wrongCenter = { x: -center.x, y: -center.y };
  const mixedCenterX = { x: -center.x, y: center.y };
  const mixedCenterY = { x: center.x, y: -center.y };
  const correct = equationLatex(center, String(radiusSquared));
  const candidates = [
    correct,
    equationLatex(center, radiusLatex),
    equationLatex(wrongCenter, String(radiusSquared)),
    equationLatex(wrongCenter, radiusLatex),
    equationLatex(mixedCenterX, String(radiusSquared)),
    equationLatex(mixedCenterY, String(radiusSquared)),
  ];

  const unique = [];
  for (const latex of candidates) {
    if (!unique.includes(latex)) unique.push(latex);
    if (unique.length === 4) break;
  }

  while (unique.length < 4) {
    const extra = equationLatex(center, String(radiusSquared + unique.length + 1));
    if (!unique.includes(extra)) unique.push(extra);
  }

  return shuffle(unique).map((latex, idx) => ({
    label: LETTERS[idx],
    latex,
    isCorrect: latex === correct,
  }));
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 24:
 *   P=(0,0) leży na okręgu o środku S=(2,4).
 *   r² = (0−2)²+(0−4)² = 20  →  (x−2)²+(y−4)² = 20  (B).
 */
function createMay2025OriginalTask() {
  const options = [
    { label: "A", latex: "(x-2)^2+(y-4)^2=2\\sqrt{5}", isCorrect: false },
    { label: "B", latex: "(x-2)^2+(y-4)^2=20", isCorrect: true },
    { label: "C", latex: "(x+2)^2+(y+4)^2=2\\sqrt{5}", isCorrect: false },
    { label: "D", latex: "(x+2)^2+(y+4)^2=20", isCorrect: false },
  ];

  return {
    type: "MaturaCircleCenterPointEquationClosedTask",
    data: {
      center: { x: 2, y: 4 },
      point: { x: 0, y: 0 },
      dx: -2,
      dy: -4,
      radiusSquared: 20,
      radiusLatex: "2\\sqrt{5}",
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 24,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return createMay2025OriginalTask();
  }

  const center = { x: randNonZero(-5, 5), y: randNonZero(-5, 5) };
  const pointAtOrigin = Math.random() < 0.5;
  let point;
  let dx;
  let dy;

  if (pointAtOrigin) {
    point = { x: 0, y: 0 };
    dx = point.x - center.x;
    dy = point.y - center.y;
  } else {
    const vector = pick(VECTOR_CASES);
    dx = vector.dx;
    dy = vector.dy;
    point = { x: center.x + dx, y: center.y + dy };
  }

  const radiusSquared = dx * dx + dy * dy;
  const radiusLatex = simplifyRoot(radiusSquared);
  const options = makeOptions(center, radiusSquared, radiusLatex);
  const correctIndex = options.findIndex((option) => option.isCorrect);

  return {
    type: "MaturaCircleCenterPointEquationClosedTask",
    data: {
      center,
      point,
      dx,
      dy,
      radiusSquared,
      radiusLatex,
      options,
      correctIndex,
    },
  };
}

export function renderTask({ data }) {
  const { center, point, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych <InlineMath math="(x,y)" /> punkt{" "}
        <InlineMath math={`P=${pointLatex(point)}`} /> leży na okręgu{" "}
        <InlineMath math="O" /> o środku w punkcie{" "}
        <InlineMath math={`S=${pointLatex(center)}`} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Okrąg <InlineMath math="O" /> jest określony równaniem
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((option) => (
          <div key={option.label} style={{ marginBottom: 8 }}>
            <InlineMath math={`${option.label}.\\; ${option.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex = userAnswer && typeof userAnswer.choiceIndex === "number" ? userAnswer.choiceIndex : null;
  const letterOptions = data.options.map((option, idx) => ({
    value: idx,
    label: option.label,
    textNode: option.label,
  }));

  const handleChange = (idx) => {
    const option = data.options[idx];
    onChange?.({
      choiceIndex: idx,
      label: option.label,
      latex: option.latex,
    });
  };

  return <ABCDInput options={letterOptions} value={choiceIndex} onChange={handleChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options?.[data.correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number" || !userAnswer.label || !userAnswer.latex) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.latex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { center, point, dx, dy, radiusSquared, radiusLatex } = data;
  const correctEquation = equationLatex(center, String(radiusSquared));

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Równanie okręgu o środku{" "}
        <InlineMath math="S=(a,b)" /> i promieniu <InlineMath math="r" /> ma postać:
      </p>

      <BlockMath math="(x-a)^2+(y-b)^2=r^2" />

      <p>
        Podstawiamy współrzędne środka <InlineMath math={`S=(${center.x},${center.y})`} />:
      </p>

      <BlockMath math={`${shiftedSquare("x", center.x)}+${shiftedSquare("y", center.y)}=r^2`} />

      <p>
        <strong>Krok 2.</strong> Punkt <InlineMath math={`P=(${point.x},${point.y})`} /> leży na okręgu,
        więc promień jest odległością między punktami <InlineMath math="S" /> i{" "}
        <InlineMath math="P" />.
      </p>

      <BlockMath math={`r=\\sqrt{(${dx})^2+(${dy})^2}`} />
      <BlockMath math={`r=\\sqrt{${radiusSquared}}${radiusLatex === `\\sqrt{${radiusSquared}}` ? "" : `=${radiusLatex}`}`} />

      <p>
        W równaniu okręgu po prawej stronie wpisujemy <InlineMath math="r^2" />, a nie{" "}
        <InlineMath math="r" />:
      </p>

      <BlockMath math={`r^2=${radiusSquared}`} />
      <BlockMath math={correctEquation} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
